import Loader from '@/components/Loader';
import {InputProps, useInput} from '@/hooks/useInput';
import useTreeChecklist, {TreeNode} from '@/hooks/useTreeChecklist';
import {Checkbox} from '@/shadcn_components/ui/checkbox';
import {Label} from '@/shadcn_components/ui/label';
import {BaseKey, useList} from '@refinedev/core';
import {useEffect} from 'react';

const translation = {
  /// subject
  inspections: 'Thanh tra',
  executions: 'Quyết định xử lý',
  exe_items: 'Hình thức xử lý',
  urges: 'Đôn đốc',
  targets: 'Đối tượng thanh tra/xử lý',
  departments: 'Phòng ban',
  exe_results: 'Kết quả xử lý',
  dashboard: 'Trang chủ',
  media: 'Tài liệu',
  members: 'Thành viên đoàn thanh tra',
  staffs: 'Nhân viên',
  users: 'Tài khoản người dùng',
  agencies: 'Cơ quan thanh tra',
  posts: 'Bài đăng',
  /// action
  '*': 'Tất cả quyền',
  read: 'Xem',
  create: 'Tạo mới',
  list: 'Xem DS',
  update: 'Sửa',
  delete: 'Xóa',
  view: 'Xem',
  reports: 'Xuất báo cáo',
};

interface PermissionItem {
  id: BaseKey;
  name: string;
}

export const PermissionInput = (props: InputProps) => {
  // input <=> form
  const {
    field: {value, onChange},
  } = useInput({...props, defaultValue: []});

  // all permissions
  const permissionListResults = useList({
    resource: 'permissions',
    pagination: {current: 0, pageSize: 9999},
  });

  const {isLoading} = permissionListResults;
  const allPermissions = permissionListResults.data?.data;

  // when interact make change
  function onTreeChange(tree: TreeNode[]) {
    console.log('tree changed', tree);

    // convert to value
    const list: PermissionItem[] = [];
    // if (tree.every(n => n.isChecked === true)) {
    // list.push({id: '1', name: '*'}); //admin
    // } else
    // {
    for (const n of tree) {
      if (n.isChecked == true) {
        list.push({id: n.id, name: n.name + '.*'});
      } else if (n.isChecked === 'indeterminate' && n.children) {
        for (const n2 of n.children) {
          if (n2.isChecked == true) {
            list.push({id: n2.id, name: n.name + '.' + n2.name});
          }
        }
      }
    }
    // }
    // onChange of value (input)
    onChange(list);
  }

  const {tree, setTree, setCheck, setCheckAll, updateNode, isAllChecked} = useTreeChecklist({
    onChange: onTreeChange,
  });

  // update tree (from list of permissions from server)
  // update value (from form's via query from server)
  useEffect(() => {
    if (!allPermissions || !value) return;
    var tree: TreeNode[] = [];
    for (var p of allPermissions) {
      const ss = p.name.split('.');
      const groupName = ss[0];
      const itemName = ss[1];

      if (groupName === '*' && !itemName) continue; //admin permission, skip

      var g = tree.find(n1 => n1.name === groupName);
      if (!g) {
        g = {
          id: '---',
          name: groupName,
          label: translation[groupName] || groupName,
          children: undefined,
          isChecked: false,
        };
        tree.push(g);
      }

      if (itemName != '*') {
        if (!g.children) g.children = [];
        g.children.push({
          id: String(p.id),
          name: itemName,
          label: translation[itemName] || itemName,
          isChecked: false,
          parent: g,
          children: undefined,
        });
      } else {
        g.id = String(p.id);
      }
    }
    console.log('all list', tree);
    updateTreeFromValue(value, tree);
    setTree(tree);
  }, [value, allPermissions]);

  function updateTreeFromValue(value: PermissionItem[], tree: TreeNode[]) {
    for (var v of value) {
      const ss = v.name.split('.');
      const groupName = ss[0];
      if (groupName === '*') {
        tree.forEach(n => updateNode(n, true));
        return;
      }

      const itemName = ss[1];

      const n = tree.find(n => n.name == groupName);
      if (n) {
        if (itemName === '*') updateNode(n, true);
        else {
          const n2 = n.children?.find(n2 => n2.name == itemName);
          if (n2) {
            updateNode(n2, true);
          }
        }
      }
    }
  }

  if (isLoading) return <Loader />;

  return (
    <>
      <Label>Phân quyền</Label>
      <table className="table w-full min-w-max table-auto text-left rounded-t-md">
        <thead>
          <tr>
            <th>
              <div className="flex flex-row items-center">
                <Checkbox
                  checked={isAllChecked}
                  onCheckedChange={checked => {
                    setCheckAll(Boolean(checked));
                  }}
                  id={'all'}
                />
                <label htmlFor={'all'} className="pl-2">
                  Chọn tất cả
                </label>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {tree.map((node, i) => (
            <tr key={i} className="hover:bg-gray-100 border-b-gray-200 border-b-2 py-2">
              {/* group name */}
              <td className="p-2 text-sm">
                <div className="flex flex-row items-center">
                  <Checkbox
                    checked={node.isChecked}
                    onCheckedChange={checked => {
                      setCheck(node, Boolean(checked));
                    }}
                    id={node.name}
                  />
                  <label htmlFor={node.name} className="pl-2">
                    {node.label}
                  </label>
                </div>
              </td>
              {/* items */}
              <td className="p-2 text-sm">
                <div className="flex flex-row items-center gap-x-4">
                  {node.children?.map((item, j) => (
                    <div key={j} className="flex flex-row items-center">
                      <Checkbox
                        checked={item.isChecked}
                        onCheckedChange={checked => {
                          setCheck(item, Boolean(checked));
                        }}
                        id={`${node.name}-${item.name}`}
                      />
                      <label htmlFor={`${node.name}-${item.name}`} className="pl-2">
                        {item.label}
                      </label>
                    </div>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};
